import { Component, OnInit } from "@angular/core";
import { marker as _ } from "@biesbjerg/ngx-translate-extract-marker";
import {
  DataService,
  FacetValueFormInputComponent,
  JobQueueService,
  JobState,
  LogicalOperator,
  ModalService,
  NotificationService,
  ProductListQueryDocument,
  TypedBaseListComponent,
  SharedModule,
  LocaleCurrencyPipe,
  CurrencyService,
  CurrencyCode,
} from "@bb-vendure/admin-ui/core";
import { EMPTY, lastValueFrom, take } from "rxjs";
import { delay, switchMap } from "rxjs/operators";
import { ProductFacetsColumnComponent } from "../product-columns/product-facets-column/product-facets-column.component";
import { ArtistColumnComponent } from "../product-columns/artist-column/artist-column.component";
import { TotalSaleColumnComponent } from "../product-columns/total-sale-column/total-sale-column.component";
import { StockQuantitiesComponent } from "../product-columns/stock-quantities-column/stock-quantites.component";
import {
  BatchCreateInSuyodaDocument,
  NewFetchProductListDocument,
} from "src/generated-gql/graphql";
import { ReindexAdvancedProductsDocument } from "../generated-graphql/graphql";
import { HttpClient } from "@angular/common/http";

@Component({
  selector: "product-list",
  standalone: true,
  imports: [
    SharedModule,
    ProductFacetsColumnComponent,
    ArtistColumnComponent,
    TotalSaleColumnComponent,
    StockQuantitiesComponent,
  ],
  templateUrl: "./product-list.component.html",
  styleUrls: ["./product-list.component.scss"],
})
export class ProductListComponent
  extends TypedBaseListComponent<
    typeof NewFetchProductListDocument,
    "productsFilterFreeGift"
  >
  implements OnInit
{
  //pendingSearchIndexUpdates = 0;
  //readonly customFields = this.getCustomFieldConfig("Product");
  readonly filters = this.createFilterCollection()
    .addIdFilter()
    .addDateFilters()
    .addFilters([
      {
        name: "enabled",
        type: { kind: "boolean" },
        label: _("catalog.visibility"),
        filterField: "enabled",
      },
      {
        name: "slug",
        type: { kind: "text" },
        label: _("common.slug"),
        filterField: "slug",
      },
    ])
    .addFilter({
      name: "facetValues",
      type: {
        kind: "custom",
        component: FacetValueFormInputComponent,
        serializeValue: (value) => value.map((v) => v.id).join(","),
        deserializeValue: (value) => value.split(",").map((id) => ({ id })),
        getLabel: (value) => {
          if (value.length === 0) {
            return "";
          }
          if (value[0].name) {
            return value.map((v) => v.name).join(", ");
          } else {
            return lastValueFrom(
              this.dataService.facet
                .getFacetValues({
                  filter: { id: { in: value.map((v) => v.id) } },
                })
                .mapSingle(({ facetValues }) =>
                  facetValues.items.map((fv) => fv.name).join(", ")
                )
            );
          }
        },
      },
      label: _("catalog.facet-values"),
      toFilterInput: (value: any[]) => ({
        facetValueId: {
          in: value.map((v) => v.id),
        },
      }),
    })
    //.addCustomFieldFilters(this.customFields)
    .connectToRoute(this.route);

  readonly sorts = this.createSortCollection()
    .defaultSort("createdAt", "DESC")
    .addSorts([
      { name: "id" },
      { name: "createdAt" },
      { name: "updatedAt" },
      { name: "name" },
      { name: "slug" },
    ])
    //.addCustomFieldSorts(this.customFields)
    .connectToRoute(this.route);

  constructor(
    protected dataService: DataService,
    private modalService: ModalService,
    private notificationService: NotificationService,
    private jobQueueService: JobQueueService,
    private currencyService: CurrencyService,
    private http: HttpClient
  ) {
    super();

    this.route.url.pipe(take(1)).subscribe((res) => {
      let soldOut = false;
      let inEvent = false;
      let unshipped = false;
      if (res.length > 0) {
        const url = res[0];
        if (url.path.includes("currently-selling")) {
          // Currently selling logic handled by server-side filtering
        } else if (url.path.includes("upcoming")) {
          // Upcoming logic handled by server-side filtering  
        } else if (url.path.includes("stock-on-hand")) {
          // Stock on hand logic handled by server-side filtering
        } else if (url.path.includes("pre-order")) {
          // Pre-order logic handled by server-side filtering
        } else if (url.path.includes("sold-out")) {
          soldOut = true;
        } else if (url.path.includes("events")) {
          inEvent = true;
        } else if (url.path.includes("unshipped")) {
          unshipped = true;
        }
      }

      this.configure({
        document: NewFetchProductListDocument,
        getItems: (data) => {
          return data.productsFilterFreeGift;
        },
        setVariables: (skip, take) => {
          const searchTerm = this.searchTermControl.value;
          let filterInput = this.filters.createFilterInput();

          if (searchTerm) {
            filterInput.name = {
              contains: searchTerm,
            };
          }
          console.log("Route-based filtering handled server-side");
          return {
            options: {
              skip,
              take,
              filter: {
                ...(filterInput ?? {}),
              },
              filterOperator: LogicalOperator.AND,
              sort: this.sorts.createSortInput(),
            },
            isFreeGift: false,
            soldOut,
            inEvent: inEvent,
            unshipped: unshipped,
          } as any;
        },
        refreshListOnChanges: [
          this.sorts.valueChanges,
          this.filters.valueChanges,
          this.searchTermControl.valueChanges,
        ],
      });
    });
  }

  batchCreateInSuyoda() {
    const ids = this.selectionManager.selection.map((p) => p.id);
    this.dataService
      .mutate(BatchCreateInSuyodaDocument, {
        ids,
      })
      .subscribe((res) => {
        if (res.batchCreateInSuyoda) {
          for (let i = 0; i < res.batchCreateInSuyoda.length; i++) {
            const { error, message } = res.batchCreateInSuyoda[i];
            if (error !== 0) {
              this.notificationService.error(message as string);
            } else {
              this.notificationService.success(message as string);
            }
          }
        }
      });
  }

  // Orignal redindex from vendure
  rebuildSearchIndex() {
    this.dataService.product.reindex().subscribe(({ reindex }) => {
      this.notificationService.info(_("catalog.reindexing"));
      this.jobQueueService.addJob(reindex.id, (job) => {
        if (job.state === JobState.COMPLETED) {
          const time = new Intl.NumberFormat().format(job.duration || 0);
          this.notificationService.success(_("catalog.reindex-successful"), {
            count: job.result.indexedItemCount,
            time,
          });
          this.refresh();
        } else {
          this.notificationService.error(_("catalog.reindex-error"));
        }
      });
    });
  }

  rebuildProductAdvancedIndex() {
    this.dataService
      .mutate(ReindexAdvancedProductsDocument)
      .subscribe(({ reindexProductAdvancedList }) => {
        this.notificationService.info(_("catalog.reindexing"));
        this.jobQueueService.addJob(reindexProductAdvancedList!.id, (job) => {
          if (job.state === JobState.COMPLETED) {
            const time = new Intl.NumberFormat().format(job.duration || 0);
            this.notificationService.success(_("catalog.reindex-successful"), {
              count: job.result.indexedItemCount,
              time,
            });
            this.refresh();
          } else {
            this.notificationService.error(_("catalog.reindex-error"));
          }
        });
      });
  }

  clearProductCache() {
    this.notificationService.info(_("catalog.clearing-cache"));
    
    this.http.get<any>('/api/products/clear-cache').subscribe({
      next: (response) => {
        if (response.success && response.data.cleared) {
          this.notificationService.success(_("catalog.cache-cleared-successfully"));
          this.refresh();
        } else {
          this.notificationService.error(_("catalog.cache-clear-failed"));
        }
      },
      error: (error) => {
        console.error('Error clearing cache:', error);
        this.notificationService.error(_("catalog.cache-clear-failed"));
      }
    });
  }

  deleteProduct(productId: string) {
    this.modalService
      .dialog({
        title: _("catalog.confirm-delete-product"),
        buttons: [
          { type: "secondary", label: _("common.cancel") },
          { type: "danger", label: _("common.delete"), returnValue: true },
        ],
      })
      .pipe(
        switchMap((response) =>
          response ? this.dataService.product.deleteProduct(productId) : EMPTY
        ),
        // Short delay to allow the product to be removed from the search index before
        // refreshing.
        delay(500)
      )
      .subscribe(
        () => {
          this.notificationService.success(_("common.notify-delete-success"), {
            entity: "Product",
          });
          this.refresh();
        },
        (err) => {
          this.notificationService.error(_("common.notify-delete-error"), {
            entity: "Product",
          });
        }
      );
  }

  productPrice(product: any) {
    const pipe = new LocaleCurrencyPipe(this.currencyService, this.dataService);
    if (!product.firstVariantPrice) {
      const result = pipe.transform(
        0,
        product.firstVariantPrice.currencyCode
      ) as string;
      if (product.firstVariantPrice.currencyCode === CurrencyCode.KRW) {
        return result.split(".")[0];
      }
      return result;
    }

    const afterTax = product.firstVariantPrice.price / 100;
    const serviceFee = product.firstVariantPrice.customFields.customServiceFee
      ? product.firstVariantPrice.customFields.customServiceFee / 100
      : 0;

    const price = Math.round(afterTax + serviceFee) * 100;

    const result = pipe.transform(
      price,
      product.firstVariantPrice.currencyCode
    ) as string;
    if (product.firstVariantPrice.currencyCode === CurrencyCode.KRW) {
      return result.split(".")[0];
    }
    return result;
  }


  canReadStatistics(entity: any) {
    const hasReadEventStatistics = this.permissionsService.userHasPermissions(['ReadEventStatistics']);
    return hasReadEventStatistics || entity.customFields.events.map((e) => e.endDate).every(d => new Date(d) < new Date());
  }

  getOrderTotal(order: any) {
    let amount = order.totalWithTax;
    if (order.state === "Cancelled") {
      amount = order.customFields.refundAmount ?? 0;
    }
  }

  // customFieldsDefault() {
  //   const excludeFields = [
  //     "startTime",
  //     "endTime",
  //     "customThreshold",
  //     "facetsColumn",
  //     "artist",
  //     "totalSalesQuantity",
  //     "totalStocks",
  //     "status",
  //   ];
  //   return this.customFields.filter((c) => {
  //     return !excludeFields.includes(c.name);
  //   });
  // }

  // ngOnInit(): void {
  //   console.log(this.customFields);
  // }
}
